import mapValues from 'lodash/mapValues';
import * as types from '../actions/types';
import { getItemCategoryId } from '../../utils/posMenuCategories';

const initialState = {
  loading: false,
  error: null,
  menuId: null, // External menu ID - fetched from gateway API
  internalMenuId: null, // Internal menu ID (_id field) - from menu map response
  menuMap: null,
  categories: [],
  items: [],
  modifierGroups: [],
  ingredients: [],
  selectedEntity: null,
  selectedEntityType: null, // 'category', 'item', 'modifierGroup'
  navigationHistory: [], // Stack of previous selections: [{ entity, entityType }, ...]
};

const posMenuReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.POS_MENU_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case types.POS_MENU_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case types.POS_MENU_SET_MENU_ID:
      return {
        ...state,
        menuId: action.payload,
      };

    case types.POS_MENU_SET_MAP: {
      // Extract internal menu ID (_id) from the menu map response
      const internalMenuId = action.payload?._id || state.internalMenuId;

      // When menu map is refreshed, update selectedEntity with fresh data if it exists
      let updatedSelectedEntity = state.selectedEntity;
      if (state.selectedEntity && state.selectedEntityType) {
        const entityId = state.selectedEntity._id;
        if (
          state.selectedEntityType === 'item' &&
          action.payload.items?.[entityId]
        ) {
          updatedSelectedEntity = action.payload.items[entityId];
        } else if (
          state.selectedEntityType === 'category' &&
          action.payload.categories?.[entityId]
        ) {
          updatedSelectedEntity = action.payload.categories[entityId];
        } else if (
          state.selectedEntityType === 'modifierGroup' &&
          action.payload.modifierGroups?.[entityId]
        ) {
          updatedSelectedEntity = action.payload.modifierGroups[entityId];
        }
      }

      return {
        ...state,
        internalMenuId,
        menuMap: action.payload,
        selectedEntity: updatedSelectedEntity,
      };
    }

    case types.POS_MENU_SET_CATEGORIES:
      return {
        ...state,
        categories: action.payload,
      };

    case types.POS_MENU_SET_ITEMS:
      return {
        ...state,
        items: action.payload,
      };

    case types.POS_MENU_SET_MODIFIER_GROUPS:
      return {
        ...state,
        modifierGroups: action.payload,
      };

    case types.POS_MENU_SET_INGREDIENTS:
      return {
        ...state,
        ingredients: action.payload,
      };

    case types.POS_MENU_ADD_CATEGORY:
      return {
        ...state,
        categories: [...state.categories, action.payload],
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              categories: {
                ...state.menuMap.categories,
                [action.payload._id]: action.payload,
              },
            }
          : null,
      };

    case types.POS_MENU_UPDATE_CATEGORY: {
      const updatedCategory = state.menuMap?.categories[action.payload._id]
        ? {
            ...state.menuMap.categories[action.payload._id],
            ...action.payload,
          }
        : action.payload;

      return {
        ...state,
        categories: state.categories.map((cat) =>
          cat._id === action.payload._id ? updatedCategory : cat
        ),
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              categories: {
                ...state.menuMap.categories,
                [action.payload._id]: updatedCategory,
              },
            }
          : null,
        // Also update selectedEntity if it's the same category being edited
        selectedEntity:
          state.selectedEntity?._id === action.payload._id
            ? updatedCategory
            : state.selectedEntity,
      };
    }

    case types.POS_MENU_DELETE_CATEGORY: {
      const newCategories = { ...state.menuMap?.categories };
      delete newCategories[action.payload];

      return {
        ...state,
        categories: state.categories.filter(
          (cat) => cat._id !== action.payload
        ),
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              categories: newCategories,
            }
          : null,
      };
    }

    case types.POS_MENU_ADD_ITEM: {
      const newItem = action.payload;
      const categoryId = newItem.category || newItem.categoryId;

      // Update category's itemIds array if item belongs to a category
      let updatedCategories = state.menuMap?.categories;
      if (categoryId && state.menuMap?.categories?.[categoryId]) {
        const category = state.menuMap.categories[categoryId];
        updatedCategories = {
          ...state.menuMap.categories,
          [categoryId]: {
            ...category,
            itemIds: [...(category.itemIds || []), newItem._id],
          },
        };
      }

      return {
        ...state,
        items: [...state.items, newItem],
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              items: {
                ...state.menuMap.items,
                [newItem._id]: newItem,
              },
              categories: updatedCategories || state.menuMap.categories,
            }
          : null,
      };
    }

    case types.POS_MENU_UPDATE_ITEM: {
      const existingItem = state.menuMap?.items[action.payload._id];

      // Handle category change: the shown category is the one being replaced,
      // the item's other categories are kept (same as the backend)
      const oldCategoryId = getItemCategoryId(action.payload._id, state.menuMap);

      // Determine the new category - preserve existing if not explicitly set in payload.
      // '' is an explicit "no category".
      const newCategoryId =
        action.payload.category !== undefined
          ? action.payload.category
          : action.payload.categoryId || oldCategoryId;

      // Merge existing item with payload, ensuring category is preserved
      const updatedItem = existingItem
        ? {
            ...existingItem,
            ...action.payload,
            category: newCategoryId, // Ensure category is always set
          }
        : {
            ...action.payload,
            category: newCategoryId,
          };

      let updatedCategories = state.menuMap?.categories;

      // Only update categories if the category is actually changing
      if (state.menuMap?.categories && oldCategoryId !== newCategoryId) {
        updatedCategories = { ...state.menuMap.categories };

        // Remove from old category
        if (oldCategoryId && updatedCategories[oldCategoryId]) {
          updatedCategories[oldCategoryId] = {
            ...updatedCategories[oldCategoryId],
            itemIds: (updatedCategories[oldCategoryId].itemIds || []).filter(
              (id) => id !== updatedItem._id
            ),
          };
        }

        // Add to new category
        if (newCategoryId && updatedCategories[newCategoryId]) {
          const newCategoryItemIds =
            updatedCategories[newCategoryId].itemIds || [];
          if (!newCategoryItemIds.includes(updatedItem._id)) {
            updatedCategories[newCategoryId] = {
              ...updatedCategories[newCategoryId],
              itemIds: [...newCategoryItemIds, updatedItem._id],
            };
          }
        }
      }

      return {
        ...state,
        items: state.items.map((item) =>
          item._id === action.payload._id ? updatedItem : item
        ),
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              items: {
                ...state.menuMap.items,
                [action.payload._id]: updatedItem,
              },
              categories: updatedCategories || state.menuMap.categories,
            }
          : null,
        // Also update selectedEntity if it's the same item being edited
        selectedEntity:
          state.selectedEntity?._id === action.payload._id
            ? updatedItem
            : state.selectedEntity,
      };
    }

    case types.POS_MENU_DELETE_ITEM: {
      const itemId = action.payload;
      const newItems = { ...state.menuMap?.items };
      delete newItems[itemId];

      // An item can sit in several categories and be an option of several groups
      const removeItemFromCategory = (category) =>
        category.itemIds?.includes(itemId)
          ? {
              ...category,
              itemIds: category.itemIds.filter((id) => id !== itemId),
            }
          : category;
      const removeItemFromModifierGroup = (group) =>
        group.optionIds?.includes(itemId) ||
        group.options?.some((opt) => opt.itemId === itemId)
          ? {
              ...group,
              optionIds: (group.optionIds || []).filter((id) => id !== itemId),
              ...(group.options && {
                options: group.options.filter((opt) => opt.itemId !== itemId),
              }),
            }
          : group;

      return {
        ...state,
        items: state.items.filter((item) => item._id !== itemId),
        categories: state.categories.map(removeItemFromCategory),
        modifierGroups: state.modifierGroups.map(removeItemFromModifierGroup),
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              items: newItems,
              categories:
                state.menuMap.categories &&
                mapValues(state.menuMap.categories, removeItemFromCategory),
              modifierGroups:
                state.menuMap.modifierGroups &&
                mapValues(
                  state.menuMap.modifierGroups,
                  removeItemFromModifierGroup
                ),
            }
          : null,
      };
    }

    case types.POS_MENU_ADD_MODIFIER_GROUP:
      return {
        ...state,
        modifierGroups: [...state.modifierGroups, action.payload],
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              modifierGroups: {
                ...state.menuMap.modifierGroups,
                [action.payload._id]: action.payload,
              },
            }
          : null,
      };

    case types.POS_MENU_UPDATE_MODIFIER_GROUP: {
      // Merge existing modifier group data with the update to preserve options/optionIds
      const existingModGroup =
        state.menuMap?.modifierGroups?.[action.payload._id];
      const mergedModGroup = existingModGroup
        ? { ...existingModGroup, ...action.payload }
        : action.payload;

      return {
        ...state,
        modifierGroups: state.modifierGroups.map((group) =>
          group._id === action.payload._id ? mergedModGroup : group
        ),
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              modifierGroups: {
                ...state.menuMap.modifierGroups,
                [action.payload._id]: mergedModGroup,
              },
            }
          : null,
        selectedEntity:
          state.selectedEntity?._id === action.payload._id
            ? mergedModGroup
            : state.selectedEntity,
      };
    }

    case types.POS_MENU_DELETE_MODIFIER_GROUP: {
      const modifierGroupId = action.payload;
      const newModifierGroups = { ...state.menuMap?.modifierGroups };
      delete newModifierGroups[modifierGroupId];

      const removeGroupFromItem = (item) =>
        item.modifierGroupIds?.includes(modifierGroupId)
          ? {
              ...item,
              modifierGroupIds: item.modifierGroupIds.filter(
                (id) => id !== modifierGroupId
              ),
            }
          : item;

      return {
        ...state,
        modifierGroups: state.modifierGroups.filter(
          (group) => group._id !== modifierGroupId
        ),
        items: state.items.map(removeGroupFromItem),
        menuMap: state.menuMap
          ? {
              ...state.menuMap,
              modifierGroups: newModifierGroups,
              items:
                state.menuMap.items &&
                mapValues(state.menuMap.items, removeGroupFromItem),
            }
          : null,
      };
    }

    case types.POS_MENU_ADD_INGREDIENT:
      return {
        ...state,
        ingredients: [...state.ingredients, action.payload],
      };

    case types.POS_MENU_UPDATE_INGREDIENT:
      return {
        ...state,
        ingredients: state.ingredients.map((ingredient) =>
          ingredient._id === action.payload._id ? action.payload : ingredient
        ),
      };

    case types.POS_MENU_DELETE_INGREDIENT:
      return {
        ...state,
        ingredients: state.ingredients.filter(
          (ingredient) => ingredient._id !== action.payload
        ),
      };

    case types.POS_MENU_SET_SELECTED_ENTITY: {
      // If skipHistory is true (e.g., clicking from tree), clear history
      // Otherwise, add current selection to history before switching
      let newHistory;
      if (action.payload.skipHistory) {
        // Clear history when selecting from tree
        newHistory = [];
      } else if (
        state.selectedEntity &&
        action.payload.entity?._id !== state.selectedEntity?._id
      ) {
        // Add current selection to history when navigating between items
        newHistory = [
          ...state.navigationHistory,
          {
            entity: state.selectedEntity,
            entityType: state.selectedEntityType,
          },
        ];
      } else {
        // Keep existing history if same item or no current selection
        newHistory = state.navigationHistory;
      }

      return {
        ...state,
        selectedEntity: action.payload.entity,
        selectedEntityType: action.payload.entityType,
        navigationHistory: newHistory,
      };
    }

    case types.POS_MENU_CLEAR_SELECTED:
      return {
        ...state,
        selectedEntity: null,
        selectedEntityType: null,
        navigationHistory: [],
      };

    case types.POS_MENU_GO_BACK: {
      // Pop the last item from history and make it the current selection
      if (state.navigationHistory.length === 0) {
        return state;
      }
      const previous =
        state.navigationHistory[state.navigationHistory.length - 1];
      const remainingHistory = state.navigationHistory.slice(0, -1);
      return {
        ...state,
        selectedEntity: previous.entity,
        selectedEntityType: previous.entityType,
        navigationHistory: remainingHistory,
      };
    }

    case types.POS_MENU_GO_TO_HISTORY_INDEX: {
      // Navigate to a specific index in history
      const targetIndex = action.payload;
      if (targetIndex < 0 || targetIndex >= state.navigationHistory.length) {
        return state;
      }
      const targetItem = state.navigationHistory[targetIndex];
      const newHistory = state.navigationHistory.slice(0, targetIndex);
      return {
        ...state,
        selectedEntity: targetItem.entity,
        selectedEntityType: targetItem.entityType,
        navigationHistory: newHistory,
      };
    }

    case types.POS_MENU_ATTACH_MODIFIER_GROUP: {
      const { itemId, modifierGroupId } = action.payload;
      if (!state.menuMap?.items?.[itemId]) {
        return state;
      }

      const item = state.menuMap.items[itemId];
      const currentModifierGroupIds = item.modifierGroupIds || [];

      // Only add if not already attached
      if (currentModifierGroupIds.includes(modifierGroupId)) {
        return state;
      }

      const updatedItem = {
        ...item,
        modifierGroupIds: [...currentModifierGroupIds, modifierGroupId],
      };

      return {
        ...state,
        items: state.items.map((i) => (i._id === itemId ? updatedItem : i)),
        menuMap: {
          ...state.menuMap,
          items: {
            ...state.menuMap.items,
            [itemId]: updatedItem,
          },
        },
        selectedEntity:
          state.selectedEntity?._id === itemId
            ? updatedItem
            : state.selectedEntity,
      };
    }

    case types.POS_MENU_DETACH_MODIFIER_GROUP: {
      const { itemId, modifierGroupId } = action.payload;
      if (!state.menuMap?.items?.[itemId]) {
        return state;
      }

      const item = state.menuMap.items[itemId];
      const currentModifierGroupIds = item.modifierGroupIds || [];

      const updatedItem = {
        ...item,
        modifierGroupIds: currentModifierGroupIds.filter(
          (id) => id !== modifierGroupId
        ),
      };

      return {
        ...state,
        items: state.items.map((i) => (i._id === itemId ? updatedItem : i)),
        menuMap: {
          ...state.menuMap,
          items: {
            ...state.menuMap.items,
            [itemId]: updatedItem,
          },
        },
        selectedEntity:
          state.selectedEntity?._id === itemId
            ? updatedItem
            : state.selectedEntity,
      };
    }

    case types.POS_MENU_ADD_MODIFIER_GROUP_OPTION: {
      const { modifierGroupId, optionId } = action.payload;
      if (!state.menuMap?.modifierGroups?.[modifierGroupId]) {
        return state;
      }

      const modifierGroup = state.menuMap.modifierGroups[modifierGroupId];
      const currentOptionIds = modifierGroup.optionIds || [];
      const currentOptions = modifierGroup.options || [];

      // Only add if not already present
      if (currentOptionIds.includes(optionId)) {
        return state;
      }

      const updatedModifierGroup = {
        ...modifierGroup,
        // Add new option at the BOTTOM of the list
        optionIds: [...currentOptionIds, optionId],
        // Also add to options array (with minimal object - full data comes from items map)
        options: [...currentOptions, { itemId: optionId }],
      };

      return {
        ...state,
        menuMap: {
          ...state.menuMap,
          modifierGroups: {
            ...state.menuMap.modifierGroups,
            [modifierGroupId]: updatedModifierGroup,
          },
        },
        modifierGroups: state.modifierGroups.map((mg) =>
          mg._id === modifierGroupId ? updatedModifierGroup : mg
        ),
      };
    }

    case types.POS_MENU_REORDER_MODIFIER_GROUP_OPTIONS: {
      const { modifierGroupId, optionIds } = action.payload;
      if (!state.menuMap?.modifierGroups?.[modifierGroupId]) {
        return state;
      }

      const modifierGroup = state.menuMap.modifierGroups[modifierGroupId];
      const updatedModifierGroup = {
        ...modifierGroup,
        optionIds,
        options: optionIds.map((id) => ({ itemId: id })),
      };

      return {
        ...state,
        menuMap: {
          ...state.menuMap,
          modifierGroups: {
            ...state.menuMap.modifierGroups,
            [modifierGroupId]: updatedModifierGroup,
          },
        },
        modifierGroups: state.modifierGroups.map((mg) =>
          mg._id === modifierGroupId ? updatedModifierGroup : mg
        ),
      };
    }

    case types.POS_MENU_REMOVE_MODIFIER_GROUP_OPTION: {
      const { modifierGroupId, optionId } = action.payload;
      // Convert to string to ensure consistent comparison
      const optionIdStr = String(optionId);

      if (!state.menuMap?.modifierGroups?.[modifierGroupId]) {
        return state;
      }

      const modifierGroup = state.menuMap.modifierGroups[modifierGroupId];
      const currentOptionIds = modifierGroup.optionIds || [];
      const currentOptions = modifierGroup.options || [];

      // Use string comparison to handle potential type mismatches
      const updatedModifierGroup = {
        ...modifierGroup,
        optionIds: currentOptionIds.filter((id) => String(id) !== optionIdStr),
        // Also filter the options array (which contains objects with itemId)
        options: currentOptions.filter(
          (opt) => String(opt.itemId) !== optionIdStr
        ),
      };

      return {
        ...state,
        menuMap: {
          ...state.menuMap,
          modifierGroups: {
            ...state.menuMap.modifierGroups,
            [modifierGroupId]: updatedModifierGroup,
          },
        },
        modifierGroups: state.modifierGroups.map((mg) =>
          mg._id === modifierGroupId ? updatedModifierGroup : mg
        ),
      };
    }

    default:
      return state;
  }
};

export default posMenuReducer;
